import Vue from "vue";
import {
  ObterProtocolos,
  ObterTiposSolicitacao,
  DetalheProtocolo,
  UploadArquivo,
  NovaSolicitacao,
  RetornoPendencia,
  ObterImagem,
  ObterDocumento,
  AvaliacaoAtendimento
} from "@/api/programasSaude";

const initialState = {
  protocolos: [],
  lastFetch: null,
  tipos: [],
  detalhe: null,
  carregando: false,
  erro: null,
  newSolicitation: {},
  successRequest: false,
  protocoloAtual: "",
  createdVinculo: "",
  uploadResponse: null,
  documentUrl: "",
  filters: {},
  activedSort: "",
  sorts: ["Mais recentes", "Mais antigos"]
};

const normalizeArrayResponse = resp => {
  if (Array.isArray(resp?.Data)) return resp.Data;
  if (Array.isArray(resp)) return resp;
  return [];
};

export default {
  namespaced: true,
  state: () => ({ ...initialState }),
  actions: {
    async fetchProtocolos(context, { force } = {}) {
      if (!force && context.state.lastFetch) return;
      Vue.set(context.state, "carregando", true);
      try {
        const resp = await ObterProtocolos({ vinculo: context.rootState.saude.vinculo });
        const data = normalizeArrayResponse(resp);
        const protocols = data.map(p => ({
          protocol: p.ProtocoloANS,
          ticket: p.Chamado,
          title: p.TipoSolicitacao,
          createdAt: p.DtSolicitacao,
          statusCode: p.CdSituacao,
          statusDsc: p.DsSituacao,
          beneficiaryName: p.DsSolicitante,
          beneficiaryCode: p.CdSolicitante,
          documentId: p.Documento ?? 0
        }));
        context.commit("SET_PROTOCOLOS", protocols);
        return data;
      } finally {
        Vue.set(context.state, "carregando", false);
      }
    },

    async fetchTiposSolicitacao(context) {
      Vue.set(context.state, "carregando", true);
      try {
        const resp = await ObterTiposSolicitacao({ vinculo: context.rootState.saude.vinculo });
        const data = normalizeArrayResponse(resp);
        context.commit("SET_TIPOS", data);
        return data;
      } finally {
        Vue.set(context.state, "carregando", false);
      }
    },

    async fetchDetalheProtocolo(context, { protocolo }) {
      Vue.set(context.state, "carregando", true);
      try {
        const resp = await DetalheProtocolo({ vinculo: context.rootState.saude.vinculo, protocolo });
        if (resp) {
          context.commit("SET_DETALHE", resp);
          return resp;
        }
        return null;
      } finally {
        Vue.set(context.state, "carregando", false);
      }
    },

    async uploadArquivo(context, { formData }) {
      return await UploadArquivo({ vinculo: context.rootState.saude.vinculo, formData });
    },

    async enviarRetornoPendencia(context, { protocolo, observacao, anexo }) {
      return await RetornoPendencia({
        vinculo: context.rootState.saude.vinculo,
        protocolo,
        observacao,
        anexo
      });
    },

    async fetchProtocolDetailImage(context, { documento, original = false }) {
      return await ObterImagem({ vinculo: context.rootState.saude.vinculo, documento, original });
    },

    async getDocumentUrl(context, { protocolo }) {
      return ObterDocumento({ vinculo: context.rootState.saude.vinculo, protocolo });
    },

    async RESET_CONTEXT({ commit }) {
      commit("RESET_STATE");
    },

    async LOGOUT_USER({ commit }) {
      commit("RESET_STATE");
    },

    async criarNovaSolicitacao(context, { pictures = [], data = {} } = {}) {
      try {
        const arquivosParaUpload = (pictures || []).filter(p => p && p.file instanceof File).map(p => p.file);

        let uploadedUrls = [];
        if (arquivosParaUpload.length) {
          const formData = new FormData();
          arquivosParaUpload.forEach((f, i) => formData.append(`anexo${i + 1}`, f, f.name));

          const uploadResp = await UploadArquivo({ vinculo: context.rootState.saude.vinculo, formData });

          context.commit("SET_UPLOAD_RESPONSE", uploadResp ?? null);
          uploadedUrls = Array.isArray(uploadResp?.Data)
            ? uploadResp.Data.map(d => d?.fileRemoteUrl).filter(Boolean)
            : [];
        }

        let ptr = 0;
        const anexosFinal = (pictures || [])
          .map(p => {
            if (p && typeof p.source === "string" && p.source.trim() !== "") return p.source;
            if (p && p.file instanceof File) return uploadedUrls[ptr++] ?? null;
            return null;
          })
          .filter(Boolean);

        const payload = {
          Tipo: data.Tipo ?? 0,
          Beneficiario: data.Beneficiario ?? "",
          Observacao: data.Observacao ?? "",
          Anexos: anexosFinal
        };

        const resp = await NovaSolicitacao({ vinculo: context.rootState.saude.vinculo, data: payload });

        const protocolo =
          resp?.protocoloANS ?? resp?.Data?.ProtocoloANS ?? resp?.Data?.protocoloANS ?? resp?.Data?.protocolo ?? "";

        if (protocolo) {
          context.commit("SET_PROTOCOLO_ATUAL", protocolo);
          context.commit("SET_SUCCESS_REQUEST", true);
        }

        return resp;
      } catch (err) {
        console.error("criarNovaSolicitacao error:", err);
      }
    },

    async avaliacao(context, { protocolo, Nota }) {
      try {
        const tipo = context.state.newSolicitation?.tipoSolicitacao ?? 0;
        const observacao = context.state.newSolicitation?.observacao ?? "";

        const resp = await AvaliacaoAtendimento({
          vinculo: context.rootState.saude.vinculo,
          atendimento: protocolo,
          tipo,
          etapa: "S",
          obs: observacao,
          respostas: [],
          Nota: Number(Nota)
        });

        return resp;
      } catch (err) {
        console.error("avaliacao error:", err);
      }
    }
  },
  mutations: {
    SET_PROTOCOLO_ATUAL(state, protocolo) {
      Vue.set(state, "protocoloAtual", protocolo);
    },
    SET_UPLOAD_RESPONSE(state, resp) {
      Vue.set(state, "uploadResponse", resp);
    },
    SET_PROTOCOLOS(state, protocolos) {
      Vue.set(state, "lastFetch", Date.now());
      Vue.set(state, "protocolos", protocolos);

      const beneficiaryFilters = [...new Set(protocolos.map(p => p.beneficiaryName))].sort();
      const statusFilters = [...new Set(protocolos.map(p => p.statusDsc))].sort();

      const filters = {
        beneficiary: beneficiaryFilters.map(f => ({
          label: f,
          isActive: state.filters.beneficiary?.find(current => current.label === f)?.isActive ?? false
        })),
        status: statusFilters.map(f => ({
          label: f,
          isActive: state.filters.status?.find(current => current.label === f)?.isActive ?? false
        }))
      };

      const beneficiaries = filters.beneficiary.filter(f => f.isActive) ?? [];
      const status = filters.status.filter(f => f.isActive) ?? [];
      filters.totalActived = beneficiaries.length + status.length;

      Vue.set(state, "filters", filters);
    },
    SET_TIPOS(state, tipos) {
      Vue.set(state, "tipos", tipos);
    },
    SET_DETALHE(state, detalhe) {
      Vue.set(state, "detalhe", detalhe);
    },
    SET_NEW_SOLICITATION(state, payload) {
      Vue.set(state, "newSolicitation", {
        ...state.newSolicitation,
        ...payload
      });
    },
    SET_SUCCESS_REQUEST(state, flag) {
      Vue.set(state, "successRequest", flag);
    },
    SET_ACTIVED_SORT(state, activedSort) {
      Vue.set(state, "activedSort", activedSort);
    },
    SET_FILTERS(state, filters) {
      Vue.set(state, "filters", filters);
    },
    CLEAN_SORT_AND_FILTERS(state) {
      const filters = JSON.parse(JSON.stringify(state.filters));
      if (filters.status) filters.status.forEach(f => (f.isActive = false));
      if (filters.beneficiary) filters.beneficiary.forEach(f => (f.isActive = false));
      filters.totalActived = 0;
      filters.selectedBeneficiary = "";
      Vue.set(state, "filters", filters);
      Vue.set(state, "activedSort", "");
    },
    RESET_STATE(state) {
      Object.keys(initialState).forEach(k => Vue.set(state, k, initialState[k]));
    },
    RESET_CONTEXT(state) {
      Object.keys(initialState).forEach(k => Vue.set(state, k, initialState[k]));
    },
    LOGOUT_USER(state) {
      Object.keys(initialState).forEach(k => Vue.set(state, k, initialState[k]));
    }
  },
  getters: {
    listaProtocolos: context => {
      let protocols = context.protocolos?.length ? JSON.parse(JSON.stringify(context.protocolos)) : [];
      if (protocols.length === 0) return [];

      const beneficiaries = context.filters?.beneficiary?.filter(f => f.isActive) ?? [];
      if (beneficiaries?.length) {
        protocols = protocols.filter(p => !!beneficiaries.find(f => f.label === p.beneficiaryName));
        if (protocols.length === 0) return [];
      }
      const status = context.filters?.status?.filter(f => f.isActive) ?? [];
      if (status?.length) {
        protocols = protocols.filter(p => !!status.find(f => f.label === p.statusDsc));
        if (protocols.length === 0) return [];
      }

      const sorts = {
        dateToNum(d) {
          if (!d || !d.includes("/")) return 0;
          const parts = d.split("/");
          return new Date(parts[2], Number(parts[1]) - 1, parts[0]).getTime();
        },
        "Mais recentes"() {
          protocols.sort((a, b) => {
            return this.dateToNum(b.createdAt) - this.dateToNum(a.createdAt);
          });
        },
        "Mais antigos"() {
          protocols.sort((a, b) => {
            return this.dateToNum(a.createdAt) - this.dateToNum(b.createdAt);
          });
        }
      };

      if (context.activedSort) sorts[context.activedSort]();

      return protocols;
    },
    tiposSolicitacao: s => s.tipos,
    detalheAtual: s => s.detalhe,
    estaCarregando: s => s.carregando,
    newSolicitation: s => s.newSolicitation,
    successRequest: s => s.successRequest,
    protocoloAtual: s => s.protocoloAtual,
    createdVinculo: s => s.createdVinculo,
    uploadResponse: s => s.uploadResponse,
    documentUrl: s => s.documentUrl,
    filters: s => s.filters,
    sorts: s => s.sorts,
    activedSort: s => s.activedSort
  }
};
